import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import AppCloseModalButton from 'components/App/AppCloseModalButton';
import { ReactComponent as CalendarIconSVG } from 'assets/images/sideBar/bx-calendar.svg';

import styles from './ManagerTax.module.scss';

const DATE_FORMAT = 'DD/MM/YYYY';

const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 4.5L6 8L9.5 4.5" stroke="#A0AEC0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function ModalAddEditTax({ isOpen, onToggle, onSubmit, itemData }) {
  const [name, setName] = useState('');
  const [rate, setRate] = useState('0');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    if (itemData) {
      setName(itemData.name || '');
      setRate(itemData.rate != null ? String(itemData.rate) : '0');

      const start = itemData.startDate || itemData.startAt;
      const end = itemData.endDate || itemData.endAt;
      setStartDate(start ? dayjs(start, 'YYYY-MM-DD') : null);
      setEndDate(end ? dayjs(end, 'YYYY-MM-DD') : null);
    } else {
      setName('');
      setRate('0');
      setStartDate(null);
      setEndDate(null);
    }
  }, [itemData, isOpen]);

  const handleNameChange = useCallback((e) => {
    const val = e.target.value;
    if (val.length <= 50) {
      setName(val);
    }
  }, []);

  const handleRateChange = useCallback((e) => {
    const val = e.target.value.replace(',', '.');
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      setRate(val);
    }
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    const startAt = startDate ? startDate.format('YYYY-MM-DD') : '';
    const endAt = endDate ? endDate.format('YYYY-MM-DD') : '';

    onSubmit({
      id: itemData?.id,
      name: name.trim() || `Vietnam VAT ${rate}%`,
      rate: String(rate),
      value: String(rate),
      startAt,
      startDate: startAt,
      endAt,
      endDate: endAt,
      status: itemData?.status || 'APPLIED',
    });
  }, [endDate, itemData, name, onSubmit, rate, startDate]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onToggle}
      centered
      className={styles.TaxModalDialog}
      width="520px"
    >
      <div className={styles.ModalHeader}>
        <h4 className={styles.ModalTitle}>
          {itemData ? 'Sửa thuế GTGT' : 'Thêm thuế GTGT'}
        </h4>
        <AppCloseModalButton onClick={onToggle} />
      </div>

      <form onSubmit={handleSubmit}>
        <ModalBody className={styles.ModalBody}>
          {/* Field 1: Tên */}
          <div className={styles.FormGroup}>
            <label htmlFor="tax-name" className={styles.FormLabel}>
              Tên
            </label>
            <div className={styles.InputWrapper}>
              <input
                id="tax-name"
                type="text"
                className={styles.TextInput}
                placeholder="Nhập nội dung"
                value={name}
                onChange={handleNameChange}
                maxLength={50}
              />
              <span className={styles.CounterText}>
                {`${name.length}/50`}
              </span>
            </div>
            <p className={styles.HelperText}>
              Tên này chỉ dành cho admin, không hiển thị cho khách hàng và người dùng
            </p>
          </div>

          {/* Field 2: Rate */}
          <div className={styles.FormGroup}>
            <label htmlFor="tax-rate" className={styles.FormLabel}>
              Rate
            </label>
            <div className={styles.InputWrapper}>
              <input
                id="tax-rate"
                type="text"
                className={styles.TextInput}
                placeholder="0"
                value={rate}
                onChange={handleRateChange}
              />
              <span className={styles.UnitSymbol}>%</span>
            </div>
          </div>

          <div className={styles.FormGroup}>
            <label className={styles.FormLabel}>
              Áp dụng từ
            </label>
            <div className={styles.DateRowWrapper}>
              <div className={styles.DatePickerWrapper}>
                <DatePicker
                  id="tax-start-date"
                  className={styles.AntDatePicker}
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  format={DATE_FORMAT}
                  placeholder="Từ ngày"
                  prefixIcon={<CalendarIconSVG className={styles.CalendarIcon} />}
                  suffixIcon={<ChevronDownIcon />}
                  allowClear
                />
              </div>
              <span className={styles.DateSeparator}>-</span>
              <div className={styles.DatePickerWrapper}>
                <DatePicker
                  id="tax-end-date"
                  className={styles.AntDatePicker}
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  format={DATE_FORMAT}
                  placeholder="Đến ngày"
                  prefixIcon={<CalendarIconSVG className={styles.CalendarIcon} />}
                  suffixIcon={<ChevronDownIcon />}
                  disabledDate={(current) => {
                    if (!startDate) return false;
                    return current && current.isBefore(startDate, 'day');
                  }}
                  allowClear
                />
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter className={styles.ModalFooter}>
          <button
            type="button"
            className={styles.BtnCancel}
            onClick={onToggle}
          >
            Huỷ
          </button>
          <button
            type="submit"
            className={styles.BtnSubmit}
          >
            Lưu
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

ModalAddEditTax.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  itemData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    rate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    startDate: PropTypes.string,
    startAt: PropTypes.string,
    endDate: PropTypes.string,
    endAt: PropTypes.string,
    status: PropTypes.string,
  }),
};

ModalAddEditTax.defaultProps = {
  itemData: null,
};

export default ModalAddEditTax;
